// src/components/TemplateSelector.tsx
import { useState, useMemo } from 'react'
import { Lock, Sparkles, Check } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { getTemplateAccessLevel, canAccessTemplate } from '../config/planLimits'
import './TemplateSelector.css'

export interface Template {
  id: number
  name: string
  description: string
  icon: string
  html: string
  variables: string[]
  tier: 'basic' | 'premium'
  category: 'welcome' | 'promo' | 'newsletter' | 'launch' | 'event' | 'feedback' | 'followup'
}

interface TemplateSelectorProps {
  onSelectTemplate: (template: Template) => void
  selectedTemplateId?: number | null
  onUpgrade?: () => void
}

// Extended templates with tier information
export const TEMPLATES: Template[] = [
  // BASIC TIER (Available for all plans)
  {
    id: 1,
    name: 'Welcome',
    description: 'Onboard new subscribers',
    icon: '👋',
    tier: 'basic',
    category: 'welcome',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h1 style="color: #1a1a1a;">Welcome to {{company}}!</h1>
  <p>Hi {{name}},</p>
  {{content}}
  <p>Best regards,<br/>The {{company}} Team</p>
  <p style="color: #999; font-size: 12px; margin-top: 30px;">Sent on {{date}}</p>
</div>`,
    variables: ['company', 'name', 'content', 'date']
  },
  {
    id: 2,
    name: 'Newsletter',
    description: 'Regular updates',
    icon: '📰',
    tier: 'basic',
    category: 'newsletter',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #1a1a1a; border-bottom: 2px solid #007bff; padding-bottom: 10px;">{{title}}</h2>
  {{content}}
  <div style="margin: 25px 0;">
    <a href="#" style="background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Read More</a>
  </div>
  <p style="color: #999; font-size: 12px;">Sent on {{date}}</p>
</div>`,
    variables: ['title', 'content', 'date']
  },
  {
    id: 3,
    name: 'Feedback Request',
    description: 'Collect reviews',
    icon: '⭐',
    tier: 'basic',
    category: 'feedback',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2>How was your experience with {{company}}?</h2>
  <p>Hi {{name}},</p>
  {{content}}
  <div style="text-align: center; margin: 30px 0;">
    <div style="font-size: 32px; letter-spacing: 8px;">⭐⭐⭐⭐⭐</div>
  </div>
  <div style="display: flex; gap: 10px; justify-content: center;">
    <a href="#" style="background: #28a745; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">Leave Feedback</a>
    <a href="#" style="background: #6c757d; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">No Thanks</a>
  </div>
  <p style="color: #999; font-size: 12px; margin-top: 30px;">Sent on {{date}}</p>
</div>`,
    variables: ['company', 'name', 'content', 'date']
  },
  
  // PREMIUM TIER (Professional and Premium plans)
  {
    id: 4,
    name: 'Promo',
    description: 'Sales & discounts',
    icon: '🎉',
    tier: 'premium',
    category: 'promo',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h1 style="color: #d32f2f;">🎉 {{title}}</h1>
  {{content}}
  <div style="text-align: center; margin: 30px 0;">
    <a href="#" style="background: #d32f2f; color: white; padding: 14px 32px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">{{ctaText}}</a>
  </div>
  <p style="color: #999; font-size: 12px;">Sent on {{date}}</p>
</div>`,
    variables: ['title', 'content', 'ctaText', 'date']
  },
  {
    id: 5,
    name: 'Product Launch',
    description: 'Announce new features',
    icon: '🚀',
    tier: 'premium',
    category: 'launch',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="text-align: center; margin-bottom: 20px;">
    <span style="font-size: 48px;">🚀</span>
  </div>
  <h1 style="color: #1a1a1a; text-align: center;">Introducing {{productName}}</h1>
  <p style="text-align: center; color: #666;">{{subtitle}}</p>
  {{content}}
  <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 25px 0;">
    <h3 style="margin-top: 0;">✨ Key Benefits:</h3>
    <ul style="padding-left: 20px;">
      {{benefits}}
    </ul>
  </div>
  <div style="text-align: center;">
    <a href="#" style="background: #6f42c1; color: white; padding: 14px 32px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">Try {{productName}} Now</a>
  </div>
  <p style="color: #999; font-size: 12px; margin-top: 30px;">Sent on {{date}}</p>
</div>`,
    variables: ['productName', 'subtitle', 'content', 'benefits', 'date']
  },
  {
    id: 6,
    name: 'Event Invite',
    description: 'Webinars & meetups',
    icon: '📅',
    tier: 'premium',
    category: 'event',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 12px; text-align: center;">
    <h1 style="margin: 0; color: white;">You're Invited!</h1>
    <p style="font-size: 18px; opacity: 0.95;">{{eventName}}</p>
  </div>
  <div style="padding: 20px;">
    <p><strong>📅 Date:</strong> {{eventDate}}</p>
    <p><strong>⏰ Time:</strong> {{eventTime}}</p>
    <p><strong>📍 Location:</strong> {{eventLocation}}</p>
    {{content}}
    <div style="text-align: center; margin: 30px 0;">
      <a href="#" style="background: #667eea; color: white; padding: 14px 32px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">RSVP Now</a>
    </div>
  </div>
  <p style="color: #999; font-size: 12px;">Sent on {{date}}</p>
</div>`,
    variables: ['eventName', 'eventDate', 'eventTime', 'eventLocation', 'content', 'date']
  },
  {
    id: 7,
    name: 'Follow-up',
    description: 'Post-purchase check-in',
    icon: '💬',
    tier: 'premium',
    category: 'followup',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2>Checking in 👋</h2>
  <p>Hi {{name}},</p>
  {{content}}
  <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; margin: 25px 0;">
    <p style="margin: 0;"><strong>💡 Quick Tip:</strong> {{tip}}</p>
  </div>
  <p>If you have any questions, just reply to this email—we're here to help!</p>
  <div style="margin: 25px 0;">
    <a href="#" style="background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Get Support</a>
  </div>
  <p style="color: #999; font-size: 12px;">Sent on {{date}}</p>
</div>`,
    variables: ['name', 'content', 'tip', 'date']
  }
]

export default function TemplateSelector({ 
  onSelectTemplate, 
  selectedTemplateId, 
  onUpgrade 
}: TemplateSelectorProps) {
  const { user } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  
  const userPlan = (user as any)?.selectedPlan || 'starter'
  const accessLevel = getTemplateAccessLevel(userPlan)
  
  const categories = useMemo(() => {
    const cats = new Set(TEMPLATES.map(t => t.category))
    return ['all', ...Array.from(cats)]
  }, [])
  
  const filteredTemplates = useMemo(() => {
    let filtered = TEMPLATES
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory)
    }
    return filtered
  }, [selectedCategory])
  
  const canAccess = (template: Template): boolean => {
    return canAccessTemplate(userPlan, template.tier)
  }
  
  const getUpgradeMessage = (template: Template): string => {
    if (template.tier === 'premium' && accessLevel === 'basic') {
      return 'Upgrade to Professional for premium templates'
    }
    return ''
  }
  
  return (
    <div className="template-selector">
      <div className="template-header">
        <h3 className="template-title">
          Email Templates
          {accessLevel === 'premium' && (
            <span className="premium-badge">
              <Sparkles size={14} /> Premium
            </span>
          )}
          {accessLevel === 'all' && (
            <span className="all-access-badge">
              <Check size={14} /> All Access
            </span>
          )}
        </h3>
        
        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="templates-grid">
        {filteredTemplates.map((template) => {
          const isAccessible = canAccess(template)
          const isSelected = selectedTemplateId === template.id
          const upgradeMessage = getUpgradeMessage(template)
          
          return (
            <div
              key={template.id}
              className={`template-card ${isSelected ? 'selected' : ''} ${!isAccessible ? 'locked' : ''}`}
              onClick={() => isAccessible && onSelectTemplate(template)}
            >
              {!isAccessible && (
                <div className="lock-overlay">
                  <Lock size={20} />
                  <span>Premium</span>
                </div>
              )}
              
              <div className="template-card-content">
                <span className="template-icon">{template.icon}</span>
                <div className="template-info">
                  <p className="template-name">
                    {template.name}
                    {template.tier === 'premium' && (
                      <Sparkles size={12} className="premium-icon" />
                    )}
                  </p>
                  <p className="template-description">{template.description}</p>
                </div>
              </div>
              
              {!isAccessible && upgradeMessage && onUpgrade && (
                <button 
                  className="upgrade-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    onUpgrade()
                  }}
                >
                  {upgradeMessage}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}